/** Compact conversation previews for the left side of the admin chat workspace. */

import PersonOutlineIcon from "@mui/icons-material/PersonOutlineOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { chatMemberName } from "~/lib/chat";
import type { ChatThreadSummary } from "~/types/chat-admin";

interface ChatThreadListProps {
  threads: ChatThreadSummary[];
  selectedId?: string;
  formatDate: (value: string) => string;
  onSelect: (id: string) => void;
}

export function ChatThreadList({
  threads,
  selectedId,
  formatDate,
  onSelect,
}: ChatThreadListProps) {
  const { t } = useTranslation();

  return (
    <Box component="ol" sx={{ listStyle: "none", margin: 0, padding: 0 }}>
      {threads.map((thread) => {
        const name = chatMemberName(thread);
        const selected = thread.id === selectedId;

        return (
          <Box
            component="li"
            key={thread.id}
            sx={{ borderBottom: "1px solid", borderColor: "divider" }}
          >
            <ButtonBase
              onClick={() => onSelect(thread.id)}
              aria-current={selected ? "page" : undefined}
              data-testid={`chat.thread-${thread.id}`}
              sx={{
                width: "100%",
                minHeight: 92,
                padding: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                textAlign: "left",
                backgroundColor: selected ? "action.selected" : "transparent",
                transition: "background-color 160ms ease",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Avatar
                aria-hidden="true"
                sx={{
                  width: 40,
                  height: 40,
                  color: "primary.main",
                  backgroundColor: "action.selected",
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 22 }} />
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="L1"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: "text.primary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                  </Typography>
                  <Typography
                    component="time"
                    dateTime={thread.updated_at}
                    variant="L3"
                    sx={{ color: "text.muted", whiteSpace: "nowrap" }}
                  >
                    {formatDate(thread.updated_at)}
                  </Typography>
                </Box>

                {thread.user_display_name ? (
                  <Typography
                    variant="T3"
                    sx={{
                      color: "text.muted",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {thread.user_email}
                  </Typography>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginTop: 0.5,
                  }}
                >
                  <Typography
                    variant="T3"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color:
                        thread.unread_count > 0
                          ? "text.primary"
                          : "text.secondary",
                      fontWeight: thread.unread_count > 0 ? 600 : 400,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {thread.last_message?.body ?? t("chat.emptyMessages")}
                  </Typography>
                  {thread.unread_count > 0 ? (
                    <Chip
                      size="small"
                      color="primary"
                      label={thread.unread_count}
                      aria-label={t("chat.unread", {
                        count: thread.unread_count,
                      })}
                      sx={{ minWidth: 28, height: 24 }}
                    />
                  ) : null}
                </Box>
              </Box>
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
}
